"use client"

import { Button } from "@/components/ui/button"
import { Root, Item } from "@radix-ui/react-radio-group"
import { FormEvent, useState } from "react"
import SupportersDialogDrawer from "./SupportersDialogDrawer"
import { InfoIcon } from "lucide-react"
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip"
import Link from "next/link"
import { Input } from "@/components/ui/input"
import { isNumeric } from "../../../../util/string"
import { formatCoins } from "../../../../util/format"
import { Supporter } from "@/lib/api/supporters"
import { useCloseSupport } from "./SupportSheetDrawer"
import { useCoinsSupport } from "@/hooks/coins/useCoinsSupport"
import { parseEther } from "viem"
import { toast } from "sonner"
import { TX_CHAIN_EXPLORER } from "@/hooks/coins/constants"

type Props = {
  supporters: Supporter[]
  coinValueInUSD: string
  poolAddress: string
  ethPrice: number
}

export default function SupportTiers({
  supporters,
  coinValueInUSD,
  poolAddress,
  ethPrice,
}: Props) {
  const [tier, setTier] = useState(tiers[1].type)
  const [loading, setLoading] = useState(false)
  const { close } = useCloseSupport()
  const [supportValue, setSupportValue] = useState(tiers[1].value)
  const { supportPost } = useCoinsSupport(poolAddress)

  const isValidSupportValue = validateSupportValue(supportValue, tier)

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    setLoading(true)
    try {
      if (!isValidSupportValue || !(ethPrice > 0))
        throw new Error("Invalid support amount")

      const amount = parseEther(String(Number(supportValue) / ethPrice))

      const { txHash, quotedAmount } = await supportPost(amount)

      toast.success(
        <div className="flex flex-col gap-1">
          <span>{`Thanks for supporting this post! You'll receive ${formatCoins(Number(quotedAmount))} coins in exchange for your support.`}</span>
          <a
            href={`${TX_CHAIN_EXPLORER}${txHash}`}
            target="_blank"
            className="underline"
          >
            View your transaction.
          </a>
        </div>
      )
    } catch (error) {
      console.error("Swap transaction failed:", error)
      if (/User rejected the request/.test(String(error))) return
      if (/Not enough balance/.test(String(error))) {
        toast.error("Not enough funds to support")
      } else {
        toast.error(
          "Something went wrong while supporting this post. Please check your wallet and try again."
        )
      }
    } finally {
      setLoading(false)
      close()
    }
  }

  return (
    <form className="flex flex-col gap-5 mt-6" onSubmit={handleSubmit}>
      <h3 className="text-sm text-wood-500 font-normal">
        How much would you like to support?
      </h3>

      <Root
        value={tier}
        className="flex flex-col w-full gap-4"
        onValueChange={(val: string) => {
          setSupportValue(tiers.filter((t) => t.type === val)[0].value)
          setTier(val as Supporter["type"])
        }}
      >
        {tiers.map((option) => {
          const typedSupporters = supporters.filter(
            (s) => s.type === option.type
          )
          return (
            <Item asChild key={option.value} value={option.type}>
              <div className="border border-border rounded-lg w-full p-5 data-[state=checked]:ring-2 data-[state=checked]:ring-cobalt cursor-pointer">
                <div className="flex justify-between items-center">
                  <div className="flex flex-col gap-2 items-start">
                    <div className="flex gap-2 text-base font-normal text-card-foreground">
                      <span>{option.emoji}</span>
                      <span>{option.label}</span>
                    </div>

                    <div className="rounded-[999px] bg-card-foreground text-xs text-primary-foreground px-3 w-fit py-1">
                      {`$${option.value}${option.type === tiers[2].type ? "+" : ""}`}
                    </div>
                  </div>

                  <SupportersDialogDrawer
                    supporters={typedSupporters}
                    isSection
                  />
                </div>

                {tier === tiers[2].type && option.type === tiers[2].type && (
                  <div className="flex flex-col w-full mt-4 gap-2">
                    <div className="relative">
                      <span className="text-muted-foreground absolute left-3 top-[13px]">
                        $
                      </span>
                      <Input
                        value={supportValue}
                        onChange={(val) => setSupportValue(val.target.value)}
                        className="px-6 py-6 text-xl font-normal border-border"
                      />
                    </div>

                    <span className="text-muted-foreground font-normal text-sm">
                      {`Enter an amount of $${tiers[2].value} or higher`}
                    </span>
                  </div>
                )}
              </div>
            </Item>
          )
        })}
      </Root>

      <Button type="submit" disabled={!isValidSupportValue || loading}>
        Support
      </Button>

      <div className="inline-block">
        <span className="text-sm text-wood-500 mr-1">
          {`You’ll receive ${isValidSupportValue ? formatCoins(Number((BigInt(Math.floor(parseFloat(supportValue) * 100)) * BigInt(1e16)) / BigInt(coinValueInUSD))).split(".")[0] : "0"} coins for supporting this`}
        </span>

        <Tooltip delayDuration={250}>
          <TooltipTrigger className="inline-block">
            <span className="text-sm text-wood-500 mr-1">post.</span>
            <InfoIcon size={14} className="text-wood-500 mb-1 inline-block" />
          </TooltipTrigger>
          <TooltipContent className="max-w-[280px] bg-card p-4 z-[51] flex flex-col gap-2 text-card-foreground border rounded-md text-sm">
            <span>What are coins?</span>
            <span>
              When you support a post, you receive coins in return. They’re a
              way to show the writer you care about their work, signal you were
              early, and express your taste.
            </span>
            <span>
              Coins are tied to this specific post and may increase in value if
              it gains popularity, but most readers collect simply to show
              appreciation and support ideas that resonate.
            </span>
            {/* TODO: Add missing link */}
            <Link target="_blank" href="" className="text-primary underline">
              Learn more about coins →
            </Link>
          </TooltipContent>
        </Tooltip>
      </div>
    </form>
  )
}

const tiers = [
  {
    value: "1",
    emoji: "👍",
    label: "Liked it",
    type: "LIKED" as Supporter["type"],
  },
  {
    value: "5",
    emoji: "❤️",
    label: "Loved it",
    type: "LOVED" as Supporter["type"],
  },
  {
    value: "10",
    emoji: "🙌",
    label: "True believer",
    type: "BELIEVER" as Supporter["type"],
  },
]

function validateSupportValue(
  supportValue: string,
  tier: Supporter["type"]
): boolean {
  // Check if the input is numeric
  if (isNumeric(supportValue)) {
    const floatValue = parseFloat(supportValue)

    // Check if the value has no more than 2 decimals
    if (supportValue.split(".")[1]?.length > 2) return false

    // Check if the value is greater than 0
    if (floatValue > 0) {
      // If it's a BELIEVER check if the value is greater than 10
      if (tier === tiers[2].type) {
        return floatValue >= parseFloat(tiers[2].value)
      }
      // Else the default value set is fine
      return true
    }
  }
  return false
}
